// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Marker, Source, useMap } from 'react-map-gl';
import centroid from '@turf/centroid';
import { AllGeoJSON } from '@turf/helpers';
import { Box, Typography } from '@mui/material';
import { getOrders } from 'Features/order/orderSelectors';
import { useAppSelector } from 'Hooks';
import stylesDef from './styles';

interface KmlLayerProps {
  fileName: string;
  data: GeoJSON.FeatureCollection;
  visible: boolean;
}

const KMLLayer = ({ fileName, data, visible }: KmlLayerProps) => {
  const sourceId = `${fileName}-kml_source`;
  const layerId = `${fileName}-kml_layer`;

  return (
    <Source id={sourceId} type="geojson" data={data}>
      <Layer
        type="fill"
        id={layerId}
        source={sourceId}
        layout={{ visibility: visible ? 'visible' : 'none' }}
        paint={{ 'fill-color': 'rgba(1, 161, 221, 0.2)' }}
      />
    </Source>
  );
};

const KML_DOT_ACTIVE = '#FF9800';
const KML_DOT_INACTIVE = '#9E9E9E';

interface OrderLabelProps {
  geojson: GeoJSON.FeatureCollection;
  visible: boolean;
  orderNumber: number;
  isActive: boolean;
  name: string;
}

const OrderLabel = ({ geojson, visible, orderNumber, isActive, name }: OrderLabelProps) => {
  const styles = stylesDef();

  const coords = useMemo(() => {
    if (!geojson?.features?.length) return null;
    try {
      return centroid(geojson as AllGeoJSON).geometry.coordinates;
    } catch {
      return null;
    }
  }, [geojson]);

  if (!visible || !coords) return null;

  return (
    <Marker longitude={coords[0]} latitude={coords[1]}>
      <Box sx={styles.labelContainer}>
        <Typography variant="body2" sx={styles.labelTitle}>
          {'Order '}
          {orderNumber}
        </Typography>
        <Box sx={styles.labelRow}>
          <Box sx={[styles.statusDot, { bgcolor: isActive ? KML_DOT_ACTIVE : KML_DOT_INACTIVE }]} />
          <Typography variant="body2" sx={styles.labelName}>
            {name}
          </Typography>
        </Box>
      </Box>
    </Marker>
  );
};

const MapKMLLayer = () => {
  const { current: map } = useMap();
  const { projectOrders, activeOrder } = useAppSelector((state) => getOrders(state));
  const totalOrders = projectOrders.length;
  const [hoveredLayerId, setHoveredLayerId] = useState<string | null>(null);

  const kmlData = useAppSelector((state) => projectOrders
    .filter((o) => o.boundaryFile)
    .map((o) => {
      const kml = state.kml.present.kmls[o._id];
      const name = o.boundaryFile.split('/').pop();
      return {
        id: kml?.id,
        visible: kml?.visible,
        geojson: kml?.featureCollection,
        name,
        layerId: `${name}-kml_layer`,
        orderNumber: totalOrders - projectOrders.indexOf(o),
        isActive: o._id === activeOrder?._id,
      };
    }));

  const layerIds = useMemo(() => kmlData.map((k) => k.layerId), [kmlData]);
  const layerIdsRef = useRef(layerIds);
  layerIdsRef.current = layerIds;

  useEffect(() => {
    if (!map) return undefined;

    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      try {
        const ids = layerIdsRef.current;
        if (ids.length === 0) {
          setHoveredLayerId(null);
          return;
        }
        const existing = ids.filter((id) => {
          try { return !!map.getLayer(id); } catch { return false; }
        });
        if (existing.length === 0) {
          setHoveredLayerId(null);
          return;
        }
        const features = map.queryRenderedFeatures(e.point, { layers: existing });
        setHoveredLayerId(features.length > 0 ? features[0].layer.id : null);
      } catch {
        // Map may be in a transitional state
      }
    };

    map.on('mousemove', onMouseMove);

    return () => {
      try { map.off('mousemove', onMouseMove); } catch { /* map destroyed */ }
    };
  }, [map]);

  return (
    <>
      {kmlData.map((kml) => (
        <KMLLayer key={kml.id} fileName={kml.name} data={kml.geojson} visible={kml.visible} />
      ))}
      {kmlData.map((kml) => (
        <OrderLabel
          key={`label-${kml.id}`}
          geojson={kml.geojson}
          visible={kml.visible && kml.layerId === hoveredLayerId}
          orderNumber={kml.orderNumber}
          isActive={kml.isActive}
          name={kml.name}
        />
      ))}
    </>
  );
};

export default MapKMLLayer;
